import {
    Alert,
    AlertIcon,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    VStack
} from '@chakra-ui/react';
import { faEnvelope } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Auth } from 'aws-amplify';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { FC, useState } from 'react';
import { useLanguage } from 'src/utils/hooks/useLanguage';
import { object, string } from 'yup';

const ForgotPasswordForm: FC = () => {
    const { t } = useTranslation('forgot_password');
    const { language } = useLanguage();
    const router = useRouter();
    const [errorMessage, setErrorMessage] = useState('');

    const validationSchema = object().shape({
        email: string().required(t('email-required')).email(t('email-invalid'))
    });

    type FormValues = {
        email: string;
    };

    const initialValues: FormValues = { email: (router.query.email as string) || '' };

    const onSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
        const email = values.email.toLowerCase();
        try {
            await Auth.forgotPassword(email);
            return router.push(
                `/${language}/confirm-forgot-password?email=${encodeURIComponent(email)}`
            );
        } catch (error: any) {
            const message = t('generic-error-message');
            setErrorMessage(message);
        }
        actions.setSubmitting(false);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}>
            {(props) => {
                const { isSubmitting } = props;

                return (
                    <Form>
                        <Flex
                            flexDirection="column"
                            height="calc(100vh - 144px)"
                            justifyContent="space-between"
                            paddingBottom={16}>
                            <VStack align="stretch" spacing={6}>
                                <Field id="email" name="email">
                                    {({ field, form }: FieldProps) => (
                                        <FormControl
                                            isInvalid={Boolean(
                                                form.errors.email && form.touched.email
                                            )}>
                                            <InputGroup>
                                                <InputLeftElement
                                                    color="lime.300"
                                                    height="100%"
                                                    pointerEvents="none"
                                                    width={16}>
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </InputLeftElement>
                                                <Input
                                                    id="email"
                                                    paddingLeft={14}
                                                    placeholder={t('email-placeholder')}
                                                    type="email"
                                                    {...field}
                                                />
                                            </InputGroup>
                                            <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                        </FormControl>
                                    )}
                                </Field>

                                {errorMessage && (
                                    <Alert bg="purple.50" borderRadius="0.935rem" status="error">
                                        <AlertIcon color="purple.400" />
                                        {errorMessage}
                                    </Alert>
                                )}
                            </VStack>
                            <Button
                                colorScheme="lime"
                                disabled={isSubmitting}
                                isLoading={isSubmitting}
                                type="submit">
                                {t('reset')}
                            </Button>
                        </Flex>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ForgotPasswordForm;
